import { useMemo } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { fmtMinutes } from './vocabulary'
import { outcomeInk, outcomeOf } from '../Moments/LedgerTable'

// What the engine heard, on the router's strip (audio-streaming step 2b).
//
// The fleet numbers say transcription is running; whether the transcript is
// RIGHT is judged the way the router is — by a person reading it beside audio
// they can play, which is their own. So each span row carries a chip saying
// what happened to its transcript (read off the span row, no second request),
// and an expanded row reads the transcript itself: one line per turn, a thin
// divider wherever the run changes (hosted diarization restarts its speaker
// labels per request, so "speaker_0" in two runs are not the same person),
// click a turn to hear it, and while a minute plays the turn under the
// playhead is lit and the word under it is bold — the ~300 ms word-drift bar,
// checked by ear. A minute with speech and no words is a router miss: that is
// the check the page exists for.
//
// Once the span is selected (step 4a), each moment the selector found is drawn
// on the words it covers, in its outcome's ink, and labelled `#N outcome` on
// the turn where it begins — N its row in the span's ledger below. A refused
// moment reads in place, beside the conversation it was cut from.

// Audio lives 30 days; a turn older than that has no minute left to play.
export const AUDIO_DAYS = 30
const NO_MOMENTS = []

const statusChip = (transcription, transcript) => {
  if (!transcription) return null
  const {
    status,
    runs,
    skipped_minutes: skipped = {},
    retry_at: retryAt,
    last_error: lastError,
  } = transcription
  switch (status) {
    case 'transcribed': {
      // A partly skipped span still has its doc; the chip says both.
      const skippedMin = Object.values(skipped).reduce((a, m) => a + m, 0)
      const skippedNote = skippedMin ? ` · ${skippedMin} min skipped` : ''
      if (!transcript) {
        return {
          label: `transcribed · ${runs} run${
            runs === 1 ? '' : 's'
          }${skippedNote}`,
        }
      }
      const speakers = new Set(
        transcript.turns.map((t) => `${t.run_index}:${t.speaker}`)
      ).size
      return {
        label: `transcribed · ${
          transcript.turns.length
        } turns · ${speakers} speaker${speakers === 1 ? '' : 's'} · ${
          transcript.asr_model
        }${skippedNote}`,
        tip: `${transcript.provider} ${transcript.asr_model} · ${
          transcript.source === 'hot' ? 'hot doc' : 'forever objects'
        } · method v${transcript.transcript_method_version}`,
      }
    }
    case 'not_yet':
      return { label: 'transcribing…', color: 'default' }
    case 'no_routed_runs':
      return { label: 'nothing routed to transcribe', color: 'default' }
    case 'skipped': {
      const why = Object.entries(skipped)
        .map(([reason, minutes]) => `${reason} ${minutes} min`)
        .join(', ')
      return { label: `skipped · ${why}`, color: 'warning' }
    }
    case 'unavailable':
      return {
        label: `provider unavailable · retry ${
          retryAt ? dayjs(retryAt).format('HH:mm') : '—'
        }`,
        color: 'warning',
        tip: lastError,
      }
    case 'unanswered':
      return {
        label: `sent, no answer · retry ${
          retryAt ? dayjs(retryAt).format('HH:mm') : '—'
        }`,
        color: 'warning',
        tip: 'Retried as halves; a range is skipped only 24 h after it first went unanswered.',
      }
    case 'rejected':
      return {
        label: `rejected · ${transcription.rejected_runs} run${
          transcription.rejected_runs === 1 ? '' : 's'
        } refused as a file`,
        color: 'error',
      }
    case 'failed':
      return { label: 'last pass failed', color: 'error', tip: lastError }
    case 'terminal':
      return { label: `gave up · ${lastError || 'terminal'}`, color: 'error' }
    default:
      return null
  }
}

export const TranscriptChip = ({ transcription, transcript }) => {
  const chip = statusChip(transcription, transcript)
  if (!chip) return null
  const node = (
    <Chip
      size="small"
      variant="outlined"
      color={chip.color || 'primary'}
      label={chip.label}
      sx={{ height: 20, fontSize: 11 }}
    />
  )
  return chip.tip ? (
    <Tooltip title={chip.tip} placement="top">
      {node}
    </Tooltip>
  ) : (
    node
  )
}

const clock = (spanStartMs, ms) => {
  const seconds = Math.max(0, Math.round((ms - spanStartMs) / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

// Which received minute holds an absolute time, for click-to-seek.
const segmentAt = (segments, ms) =>
  segments.find(
    (s) =>
      s.start_ms != null && s.start_ms <= ms && ms < s.start_ms + s.duration_ms
  )

// One minute's share of the transcript, under the minute that was clicked: the
// words that start inside it, grouped by speaker, and the events heard there.
// Click a word to hear the minute from it; while the minute plays, the word
// under the playhead is bold.
export const MinuteHeard = ({
  segment,
  span,
  transcript,
  loading,
  error,
  playing,
  position,
  onSeek,
}) => {
  const theme = useTheme()
  const start = segment.start_ms ?? segment.seg_start_ms
  const end = start + (segment.duration_ms || 60_000)
  const playhead = playing?.name === segment.name ? start + position : null
  const seekable = !segment.expired && start != null

  const groups = useMemo(() => {
    if (!transcript || start == null) return []
    const words = transcript.words
      .filter((w) => w.start_ms >= start && w.start_ms < end)
      .sort((a, b) => a.start_ms - b.start_ms)
    const out = []
    words.forEach((word) => {
      const last = out[out.length - 1]
      if (
        last &&
        last.run_index === word.run_index &&
        last.speaker === word.speaker
      ) {
        last.words.push(word)
      } else {
        out.push({
          run_index: word.run_index,
          speaker: word.speaker,
          start_ms: word.start_ms,
          words: [word],
        })
      }
    })
    return out
  }, [transcript, start, end])
  const events = transcript
    ? transcript.events.filter((e) => e.start_ms >= start && e.start_ms < end)
    : []

  let body
  if (loading) {
    body = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">
          reading what the engine heard
        </Typography>
      </Box>
    )
  } else if (error) {
    body = (
      <Typography variant="caption" color="text.secondary">
        {error}
      </Typography>
    )
  } else if (!transcript) {
    body = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          No transcript for this span
        </Typography>
        <TranscriptChip transcription={span.transcription} />
      </Box>
    )
  } else if (!groups.length) {
    body = (
      <Typography variant="caption" color="text.secondary">
        {segment.decision === 'route'
          ? 'Routed and transcribed: no words in this minute.'
          : 'The router skipped this minute, so it was not transcribed.'}
      </Typography>
    )
  } else {
    body = groups.map((group) => {
      const live =
        playhead != null &&
        group.start_ms <= playhead &&
        playhead <= group.words[group.words.length - 1].end_ms
      return (
        <Box
          key={`${group.run_index}-${group.start_ms}`}
          sx={{
            display: 'flex',
            gap: 1.5,
            py: 0.25,
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: live
              ? alpha(theme.palette.primary.main, 0.08)
              : 'transparent',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', minWidth: 40 }}
          >
            {clock(span.start_ms, group.start_ms)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 72, fontFamily: 'monospace' }}
          >
            {group.speaker}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 13, flex: 1 }}>
            {group.words.map((word, i) => {
              const bold =
                playhead != null &&
                playhead >= word.start_ms &&
                playhead <= word.end_ms
              return (
                <span
                  key={`${word.start_ms}-${i}`}
                  onClick={() =>
                    seekable && onSeek(segment, word.start_ms - start)
                  }
                  style={{
                    fontWeight: bold ? 700 : 400,
                    cursor: seekable ? 'pointer' : 'default',
                  }}
                >
                  {i ? ' ' : ''}
                  {word.text}
                </span>
              )
            })}
          </Typography>
        </Box>
      )
    })
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '150px 1fr',
        gap: 1.5,
        alignItems: 'start',
        pb: 0.75,
      }}
    >
      <Box>
        <Typography variant="caption" fontWeight={700} display="block">
          Engine heard
        </Typography>
        {transcript && (
          <Typography variant="caption" color="text.secondary" display="block">
            {transcript.asr_model}
            {events.length
              ? ` · ${[...new Set(events.map((e) => e.text))].join(' ')}`
              : ''}
          </Typography>
        )}
      </Box>
      <Box>{body}</Box>
    </Box>
  )
}

const EngineHeard = ({
  span,
  transcript,
  loading,
  error,
  playing,
  position,
  onSeek,
  moments = NO_MOMENTS,
}) => {
  const theme = useTheme()
  // The absolute playhead: the playing minute's start plus the player's
  // position inside it.
  const playhead =
    playing && playing.start_ms != null ? playing.start_ms + position : null
  const expired = span.start_ms < Date.now() - AUDIO_DAYS * 86_400_000

  // Words grouped under their turn once, not per render of the playhead.
  const turns = useMemo(() => {
    if (!transcript) return []
    return transcript.turns.map((turn) => ({
      ...turn,
      words: transcript.words.filter(
        (w) =>
          w.run_index === turn.run_index &&
          w.speaker === turn.speaker &&
          w.start_ms >= turn.start_ms &&
          w.end_ms <= turn.end_ms
      ),
    }))
  }, [transcript])

  // Which moments begin on which turn: the first turn the moment's window
  // overlaps.
  const opens = useMemo(() => {
    const byTurn = {}
    moments.forEach((moment, index) => {
      const at = turns.findIndex(
        (turn) => turn.start_ms < moment.end_ms && turn.end_ms > moment.start_ms
      )
      if (at >= 0) byTurn[at] = [...(byTurn[at] || []), index]
    })
    return byTurn
  }, [moments, turns])
  const momentAt = (ms) =>
    moments.find((moment) => ms >= moment.start_ms && ms < moment.end_ms)

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">
          reading what the engine heard
        </Typography>
      </Box>
    )
  }
  if (error) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 2 }}
      >
        What the engine heard: {error}
      </Typography>
    )
  }
  if (!transcript) return null

  const stats = transcript.stats || {}
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
        What the engine heard
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        {transcript.provider} {transcript.asr_model} ·{' '}
        {fmtMinutes(stats.transcribed_ms || 0)} transcribed of{' '}
        {fmtMinutes(stats.routed_ms || 0)} routed
        {stats.skipped_ms ? ` · ${fmtMinutes(stats.skipped_ms)} skipped` : ''}
        {stats.bulk_ms
          ? ` · ${fmtMinutes(stats.bulk_ms)} under the floors`
          : ''}
        {transcript.events?.length
          ? ` · ${transcript.events.length} events`
          : ''}
        {transcript.source === 'forever'
          ? ' · read from the forever objects'
          : ''}
        . Click a turn to hear it; the word under the playhead is bold.
      </Typography>
      {turns.map((turn, index) => {
        const newRun =
          index > 0 && turn.run_index !== turns[index - 1].run_index
        const live =
          playhead != null &&
          turn.start_ms <= playhead &&
          playhead <= turn.end_ms
        const segment = segmentAt(span.segments, turn.start_ms)
        return (
          <Box key={`${turn.run_index}-${turn.start_ms}`}>
            {newRun && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderTop: '1px dashed',
                    borderColor: 'divider',
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 10 }}
                >
                  run {turn.run_index} · speaker labels reset
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    borderTop: '1px dashed',
                    borderColor: 'divider',
                  }}
                />
              </Box>
            )}
            <Box
              onClick={() =>
                segment &&
                !expired &&
                onSeek(segment, turn.start_ms - segment.start_ms)
              }
              sx={{
                display: 'flex',
                gap: 1.5,
                py: 0.25,
                px: 0.5,
                borderRadius: 0.5,
                cursor: segment && !expired ? 'pointer' : 'default',
                opacity: expired ? 0.5 : 1,
                bgcolor: live
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'transparent',
                '&:hover':
                  segment && !expired
                    ? { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                    : {},
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace', minWidth: 40 }}
              >
                {clock(span.start_ms, turn.start_ms)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 72, fontFamily: 'monospace' }}
              >
                {turn.speaker}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13, flex: 1 }}>
                {(opens[index] || []).map((at) => (
                  <Box
                    key={at}
                    component="span"
                    sx={{
                      mr: 0.75,
                      px: 0.5,
                      borderRadius: 0.5,
                      fontSize: 10,
                      fontWeight: 700,
                      color: theme.palette.background.paper,
                      bgcolor: outcomeInk(theme, moments[at].outcome),
                    }}
                  >
                    #{at + 1} {outcomeOf(moments[at].outcome).label}
                  </Box>
                ))}
                {turn.words.length
                  ? turn.words.map((word, i) => {
                      const bold =
                        live &&
                        playhead >= word.start_ms &&
                        playhead <= word.end_ms
                      const found = momentAt(word.start_ms)
                      return (
                        <span
                          key={`${word.start_ms}-${i}`}
                          style={{ fontWeight: bold ? 700 : 400 }}
                        >
                          {i ? ' ' : ''}
                          <span
                            style={
                              found
                                ? {
                                    backgroundColor: alpha(
                                      outcomeInk(theme, found.outcome),
                                      0.18
                                    ),
                                    borderRadius: 3,
                                  }
                                : undefined
                            }
                          >
                            {word.text}
                          </span>
                        </span>
                      )
                    })
                  : turn.text}
              </Typography>
            </Box>
          </Box>
        )
      })}
      {expired && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1 }}
        >
          This span's audio is past its 30 days; the words are kept, the minutes
          are gone.
        </Typography>
      )}
    </Box>
  )
}

export default EngineHeard
